import { useState } from 'react';
// @mui
import {
  Stack,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import ExportIcon from '../../../assets/icons/ExportIcon';
// hooks
import { useAuthContext } from '../../../auth/useAuthContext';
import { useSnackbar } from '../../../components/snackbar';
// services
import { DirectDebitsService } from '../../../api/directDebits.service';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Failed', value: 'Failed' },
];

const PAYMENT_STATUS_OPTIONS = [
  { label: 'On track', value: 'On track' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Failed', value: 'Failed' },
];

type Props = {
  isFiltered: boolean;
  filterName: string;
  filterStatus: string[];
  onFilterName: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterStatus: (value: string[]) => void;
  onResetFilter: VoidFunction;
};

export default function DirectDebitsTableToolbar({
  isFiltered,
  filterName,
  filterStatus,
  onFilterName,
  onFilterStatus,
  onResetFilter,
}: Props) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isExporting, setIsExporting] = useState(false);

  const directDebitsService = new DirectDebitsService();

  const handleExport = async () => {
    if (!user?.isDemo) {
      setIsExporting(true);
      try {
        const response = await directDebitsService.exportDirectDebitRecords();
        
        // Create and download CSV file
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `direct-debits-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        enqueueSnackbar('Direct debits exported successfully!', { variant: 'success' });
      } catch (error) {
        console.error('Export failed:', error);
        enqueueSnackbar('Failed to export direct debits', { variant: 'error' });
      } finally {
        setIsExporting(false);
      }
    } else {
      // Mock export for demo
      const mockData = [
        ['Client Name', 'Client ID', 'Direct Debit Status', 'Payment Amount', 'Next Payment Due Date', 'Payment Status'],
        ['Saad', '10190', 'Pending', '1000', '21/2/2024', 'overdue'],
        ['Muhammad Umar', '10145', 'Active', '2000', '23/12/2024', 'Failed'],
        ['Muhammad Tauseef', '780456', 'Cancelled', '3000', '21/12/2024', 'On track'],
      ];
      
      const csvContent = mockData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `direct-debits-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Direct debits exported successfully!', { variant: 'success' });
    }
  };

  return (
    <Stack
      spacing={2}
      alignItems="center"
      direction={{
        xs: 'column',
        sm: 'row',
      }}
      sx={{ px: 2.5, py: 3 }}
    >
      <TextField
        fullWidth
        value={filterName}
        onChange={onFilterName}
        placeholder="Search by client name, phone and email..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        options={STATUS_OPTIONS}
        getOptionLabel={(option) => option.label}
        value={STATUS_OPTIONS.filter((option) => filterStatus.includes(option.value))}
        onChange={(event, newValue) => {
          onFilterStatus(newValue.map((option) => option.value));
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Order Status" 
            placeholder={filterStatus.length === 0 ? "Select status" : ""} 
          />
        )}
        sx={{
          width: 234,
          minWidth: 234,
          textTransform: 'capitalize',
        }}
      />

      {isExporting ? (
        <IconButton
          color="default"
          sx={{
            p: 0,
            backgroundColor: "white",
            width: "40px",
            height: "40px",
            borderRadius: "15px",
          }}>
          <CircularProgress size={24} color="inherit" />
        </IconButton>
      ) : (
        <Tooltip title="Export List">
          <IconButton color="default" onClick={handleExport} sx={{ p: 0 }}>
            <ExportIcon />
          </IconButton>
        </Tooltip>
      )}

      {isFiltered && (
        <Button
          color="error"
          sx={{ flexShrink: 0 }}
          onClick={onResetFilter}
          startIcon={<Iconify icon="eva:trash-2-outline" />}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}